import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class KanbanOverlayService {
  private static readonly STORAGE_KEY = 'kanban_mode_open';

  private isOpenSub = new BehaviorSubject<boolean>(false);
  public isOpen$ = this.isOpenSub.asObservable();

  private kanbanFilterChangedSub = new Subject<void>();
  public kanbanFilterChanged$ = this.kanbanFilterChangedSub.asObservable();

  private isMinimizedSub = new BehaviorSubject<boolean>(false);
  public isMinimized$ = this.isMinimizedSub.asObservable();

  private isSplitViewSub = new BehaviorSubject<boolean>(false);
  public isSplitView$ = this.isSplitViewSub.asObservable();

  private showConfigSub = new BehaviorSubject<boolean>(false);
  public showConfig$ = this.showConfigSub.asObservable();

  private pipelineNameSub = new BehaviorSubject<string | null>(null);
  public pipelineName$ = this.pipelineNameSub.asObservable();

  private pipelineLegacyFormatSub = new BehaviorSubject<boolean>(false);
  public pipelineLegacyFormat$ = this.pipelineLegacyFormatSub.asObservable();

  private pipelineToEditSub = new BehaviorSubject<string | null>(null);
  public pipelineToEdit$ = this.pipelineToEditSub.asObservable();

  public get isOpen(): boolean {
    return this.isOpenSub.value;
  }

  public get isMinimized(): boolean {
    return this.isMinimizedSub.value;
  }

  public get isSplitView(): boolean {
    return this.isSplitViewSub.value;
  }

  public open(): void {
    this.isMinimizedSub.next(false);
    this.isOpenSub.next(true);
    localStorage.setItem(KanbanOverlayService.STORAGE_KEY, 'true');
  }

  public close(): void {
    this.isSplitViewSub.next(false);
    this.isMinimizedSub.next(false);
    this.isOpenSub.next(false);
    this.pipelineLegacyFormatSub.next(false);
    this.pipelineToEditSub.next(null);
    localStorage.removeItem(KanbanOverlayService.STORAGE_KEY);
  }

  public toggle(): void {
    this.isOpenSub.next(!this.isOpenSub.value);
  }

  public minimize(): void {
    this.isSplitViewSub.next(false);
    this.isMinimizedSub.next(true);
  }

  public restore(): void {
    this.isSplitViewSub.next(false);
    this.isMinimizedSub.next(false);
  }

  public splitView(): void {
    this.isMinimizedSub.next(false);
    this.isSplitViewSub.next(true);
  }

  public exitSplitView(): void {
    this.isSplitViewSub.next(false);
  }

  public toggleConfig(): void {
    this.showConfigSub.next(!this.showConfigSub.value);
  }

  public setShowConfig(value: boolean): void {
    this.showConfigSub.next(value);
  }

  public setPipelineName(name: string | null): void {
    this.pipelineNameSub.next(name);
  }

  public setPipelineLegacyFormat(isLegacyFormat: boolean): void {
    this.pipelineLegacyFormatSub.next(isLegacyFormat);
  }

  public wasOpenBeforeReload(): boolean {
    return localStorage.getItem(KanbanOverlayService.STORAGE_KEY) === 'true';
  }

  public notifyKanbanFilterChanged(): void {
    this.kanbanFilterChangedSub.next();
  }

  public setPipelineToEdit(pipelineId: string | null): void {
    this.pipelineToEditSub.next(pipelineId);
  }

  public clearPipelineToEdit(): void {
    this.pipelineToEditSub.next(null);
  }
}
